import { useState } from 'react';
import { toast } from 'react-toastify';

import { AuthService } from 'api/services/authService';
import { useAuthStore } from 'store/authStore';
import { useMenuStore } from 'store/menuStore';
import { homePathFor } from 'routes/homeByProfile';

// ============================================================================
// Franja de emulación
// ============================================================================
//
// Mientras el superadmin ve el sistema como otro usuario, esta franja está
// SIEMPRE visible. No es decoración: es lo único que distingue "estoy
// revisando lo que ve el auditor" de "estoy en mi sesión", y de esa distinción
// depende que nadie modifique datos creyendo que está en una vista de prueba.
//
// Por eso no se puede cerrar ni esconder, y por eso el aviso dice el nombre del
// usuario emulado y no solo "modo emulación".
//
// Quién está detrás lo confirma el servidor en cada /auth/check leyendo el
// claim del token, así que la franja sobrevive a una recarga y no se puede
// apagar borrando lo que el navegador guardó.
// ============================================================================

export default function ImpersonationBanner() {
  const impersonatedBy = useAuthStore((s) => s.impersonatedBy);
  const user = useAuthStore((s) => s.user);
  const setImpersonation = useAuthStore((s) => s.setImpersonation);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const clearMenuItems = useMenuStore((s) => s.clearItems);
  const [saliendo, setSaliendo] = useState(false);

  if (!impersonatedBy) return null;

  const salir = async () => {
    setSaliendo(true);
    try {
      const s = await AuthService.stopImpersonation();
      setImpersonation(s.token, s.user, null);
      clearMenuItems();
      // Recarga completa por lo mismo que al entrar: hay que montar la
      // aplicación de cero para que no quede nada del usuario emulado.
      window.location.href = homePathFor(s.user.keyProfile);
    } catch {
      // Si el servidor ya no puede devolver la sesión —al superadmin le
      // quitaron la bandera, o el token venció— lo honesto es mandar a iniciar
      // sesión, no dejarlo atrapado en la piel de otro.
      setSaliendo(false);
      toast.error('No se pudo volver a tu sesión. Inicia sesión de nuevo.');
      clearAuth();
      clearMenuItems();
      window.location.href = '/login';
    }
  };

  const quien = [user?.name, user?.firstLastName].filter(Boolean).join(' ') || 'otro usuario';

  return (
    <div
      className="d-flex flex-wrap align-items-center justify-content-center gap-2 px-3 py-2"
      // Colores quemados y no una clase de alerta: esta franja tiene que
      // verse igual en tema claro y oscuro, y la variante `warning` del tema
      // pinta texto claro sobre fondo claro en uno de los dos.
      style={{
        background: '#ffb64d', color: '#1b1b2f',
        fontSize: '.9375rem', fontWeight: 500,
        position: 'sticky', top: 0, zIndex: 1040,
      }}
      role="status"
    >
      <span>
        <i className="ph ph-user-switch me-2" />
        Estás viendo el sistema como <strong>{quien}</strong>
        {user?.profileName ? <> ({user.profileName})</> : null}
        {impersonatedBy.name ? <> — emulación de {impersonatedBy.name}</> : null}
      </span>
      <button
        type="button" className="btn btn-sm btn-dark"
        style={{ fontSize: '.8125rem' }}
        onClick={salir} disabled={saliendo}
      >
        {saliendo
          ? <><i className="ph ph-spinner me-1" />Volviendo…</>
          : <><i className="ph ph-arrow-u-up-left me-1" />Volver a mi sesión</>}
      </button>
    </div>
  );
}
